/**
 * MAP VIEW COMPONENT
 * Interactive map component for displaying camera registrations using
 * the Google Maps JavaScript API (@vis.gl/react-google-maps).
 *
 * Features:
 * - Hover to show tooltip with registrant name
 * - Color-coded markers by status
 * - Auto-fits bounds to show all markers
 * - Bilingual failure message when the API key is missing or the map fails to load
 * - Bilingual configuration notice when REACT_APP_ADMIN_MAP_ID is not set at build time
 *
 * PRIVACY: Only each marker's latitude/longitude is passed to the map
 * library. Names, emails, phone numbers, camera descriptions and coverage
 * text are rendered in application-controlled overlays (AdvancedMarker
 * children and InfoWindow content), not in any Google API request.
 */

import React, { useMemo, useState } from 'react';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  InfoWindow,
  useMap,
} from '@vis.gl/react-google-maps';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { MapPin, Camera, Phone, Mail, Home, Building, Tractor, Trees, Shield, AlertTriangle, Eye } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';

// Status-based marker colors (unchanged from the Leaflet version)
const MARKER_COLORS = {
  new: '#3B82F6',      // Blue - New registrations
  reviewed: '#F59E0B', // Orange - Under review
  active: '#10B981',   // Green - Active
  inactive: '#6B7280'  // Gray - Inactive
};

// Status labels
const STATUS_LABELS = {
  en: {
    new: 'New',
    reviewed: 'Reviewed',
    active: 'Active',
    inactive: 'Inactive'
  },
  fr: {
    new: 'Nouveau',
    reviewed: 'Examiné',
    active: 'Actif',
    inactive: 'Inactif'
  }
};

// Property type icons
const getPropertyIcon = (type) => {
  switch(type) {
    case 'commercial': return Building;
    case 'farm': return Tractor;
    case 'acreage': return Trees;
    default: return Home;
  }
};

// Default center (Winnipeg, Manitoba)
const DEFAULT_CENTER = { lat: 49.8951, lng: -97.1384 };
const DEFAULT_ZOOM = 6;

// Single coordinate-validation rule shared by displayed markers and the
// marker count. Ranges reject nonsense values; finite checks reject NaN and
// Infinity. Zero is a legitimate coordinate and passes in both directions.
const isValidCoord = (value, min, max) =>
  typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;

const hasValidCoords = (marker) =>
  isValidCoord(marker.latitude, -90, 90) &&
  isValidCoord(marker.longitude, -180, 180);

// Fit the viewport to all markers once they change
const FitBounds = ({ markers }) => {
  const map = useMap();

  const bounds = useMemo(() => {
    if (!markers || markers.length === 0) return null;
    const b = new window.google.maps.LatLngBounds();
    markers.forEach(m => b.extend({ lat: m.latitude, lng: m.longitude }));
    return b;
  }, [markers]);

  React.useEffect(() => {
    if (bounds && map) {
      map.fitBounds(bounds, 50);
    }
  }, [bounds, map]);

  return null;
};

// Notice shown when the API key is missing, the map cannot load, or the
// configured Map ID was not provided at build time. The Map ID problem gets
// its own bilingual message so operators can tell a build-configuration gap
// apart from a missing or blocked API key.
const MapFailureNotice = ({ missingMapId }) => {
  const { language } = useLanguage();
  return (
    <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center px-6 py-12 bg-gray-50 rounded-lg border border-gray-200">
      <AlertTriangle className="w-10 h-10 text-amber-500 mb-4" />
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        {missingMapId
          ? (language === 'en' ? 'Map configuration incomplete' : 'Configuration de la carte incomplète')
          : (language === 'en' ? 'Map unavailable' : 'Carte indisponible')}
      </h3>
      <p className="text-sm text-gray-600 max-w-md">
        {missingMapId
          ? (language === 'en'
            ? 'No map ID was supplied when the frontend was built. REACT_APP_ADMIN_MAP_ID must be set at build time to a JavaScript Map ID created in Google Cloud. The registration table and all other dashboard features remain available.'
            : 'Aucun identifiant de carte n\'a été fourni lors de la compilation du frontend. REACT_APP_ADMIN_MAP_ID doit être défini au moment de la compilation avec un identifiant de carte créé dans Google Cloud. Le tableau des inscriptions et toutes les autres fonctions du tableau de bord restent disponibles.')
          : (language === 'en'
            ? 'The map could not be loaded. The Google Maps API key may be missing, invalid, or blocked. The registration table and all other dashboard features remain available.'
            : 'La carte n\'a pas pu être chargée. La clé API Google Maps est peut-être manquante, invalide ou bloquée. Le tableau des inscriptions et toutes les autres fonctions du tableau de bord restent disponibles.')}
      </p>
    </div>
  );
};

// Main MapView component
const MapView = ({ markers, onViewDetails }) => {
  const { language } = useLanguage();
  const t = translations[language];
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);

  // Empty or whitespace-only values must render the bilingual "map
  // unavailable" fallback rather than attempting Google authentication.
  // Note: never compare against the placeholder literal here - env.sh's
  // global substitution would rewrite that comparison too.
  const apiKey = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '').trim() || undefined;

  // Admin Map ID (cloud-console map styling). REACT_APP_ADMIN_MAP_ID must
  // contain a JavaScript Map ID created in Google Cloud and is supplied at
  // frontend build time (inlined by Create React App; NOT runtime-injectable
  // like the API key - env.sh only rewrites the key placeholder). When it is
  // missing the map cannot be styled as configured, so a bilingual notice is
  // shown instead of silently substituting an arbitrary ID or DEMO_MAP_ID.
  const mapId = (process.env.REACT_APP_ADMIN_MAP_ID || '').trim();

  const getMarkerColor = (status) => MARKER_COLORS[status] || MARKER_COLORS.new;

  const getStatusBadge = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800 border-blue-200',
      reviewed: 'bg-orange-100 text-orange-800 border-orange-200',
      active: 'bg-green-100 text-green-800 border-green-200',
      inactive: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[status] || colors.new;
  };

  // Google's built-in map controls are localized to the language the Maps
  // JS API was loaded with, and the API loads only once per page session
  // (language is first-render-only; remounting APIProvider does not reload
  // it). So the API language is fixed once for the session and switching
  // EN/FR at runtime updates only application-rendered text. Changing the
  // built-in control language requires a full page reload in the other
  // language — never done automatically, so dashboard state is preserved.
  const [sessionLanguage] = useState(language === 'fr' ? 'fr-CA' : 'en-CA');

  if (!apiKey || !mapId) {
    return (
      <div className="map-container" style={{ height: '600px', width: '100%', position: 'relative' }}>
        <MapFailureNotice missingMapId={!mapId} />
      </div>
    );
  }

  const validMarkers = (markers || []).filter(hasValidCoords);

  // Resolved from the selected marker's own property type so the info window
  // (rendered outside the marker loop) cannot reference a loop-scoped icon.
  // getPropertyIcon falls back to the residential icon for unknown types.
  const SelectedIcon = getPropertyIcon(selectedMarker?.propertyType);

  return (
    <div className="map-container" style={{ height: '600px', width: '100%', position: 'relative' }}>
      <APIProvider
        apiKey={apiKey}
        language={sessionLanguage}
        region="CA"
        onLoad={() => console.info('Google Maps JS API loaded')}
        onError={() => console.error('Google Maps JS API failed to load')}
      >
        <Map
          defaultCenter={DEFAULT_CENTER}
          defaultZoom={DEFAULT_ZOOM}
          mapId={mapId}
          style={{ height: '100%', width: '100%', borderRadius: '8px' }}
          disableDefaultUI={false}
          scrollWheelZoom={true}
          gestureHandling={'cooperative'}
        >
          <FitBounds markers={validMarkers} />

          {validMarkers.map((marker) => {
            const PropertyIcon = getPropertyIcon(marker.propertyType);
            const position = { lat: marker.latitude, lng: marker.longitude };

            return (
              <AdvancedMarker
                key={marker.id}
                position={position}
                title={marker.municipality || ''}
                onMouseEnter={() => setHoveredId(marker.id)}
                onMouseLeave={() => setHoveredId(null)}
                onClick={() => setSelectedMarker(marker)}
              >
                {/* Custom pin keeps the existing status colour coding.
                    Application-controlled tooltip replaces the Leaflet
                    hover tooltip; nothing here is sent to Google. */}
                <div
                  style={{
                    backgroundColor: getMarkerColor(marker.status),
                    width: hoveredId === marker.id ? 30 : 24,
                    height: hoveredId === marker.id ? 30 : 24,
                    borderRadius: '50%',
                    border: '3px solid white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer'
                  }}
                />
              </AdvancedMarker>
            );
          })}

          {/* Click popup with full details - application-controlled content */}
          {selectedMarker && (
            <InfoWindow
              position={{ lat: selectedMarker.latitude, lng: selectedMarker.longitude }}
              onCloseClick={() => setSelectedMarker(null)}
              headerContent={
                <div className="flex items-center justify-between gap-3">
                  <span className="font-bold text-base">
                    {selectedMarker.firstName} {selectedMarker.lastName}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${getStatusBadge(selectedMarker.status)}`}>
                    {STATUS_LABELS[language][selectedMarker.status] || STATUS_LABELS[language].new}
                  </span>
                </div>
              }
            >
              <div className="p-1">
                {/* Property Info */}
                <div className="space-y-2 mb-3">
                  <div className="flex items-center gap-2 text-sm">
                    <SelectedIcon className="w-4 h-4 text-gray-500" />
                    <span className="capitalize font-medium">{selectedMarker.propertyType || 'Residential'}</span>
                    {selectedMarker.municipality && (
                      <span className="text-gray-500">• {selectedMarker.municipality}</span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <Camera className="w-4 h-4 text-blue-600" />
                    <span>
                      <strong>{selectedMarker.numberOfCameras}</strong> {language === 'en' ? 'camera(s)' : 'caméra(s)'}
                    </span>
                  </div>

                  {selectedMarker.nearestDetachment && (
                    <div className="flex items-center gap-2 text-sm">
                      <Shield className="w-4 h-4 text-red-600" />
                      <span className="text-gray-600">{selectedMarker.nearestDetachment}</span>
                    </div>
                  )}
                </div>

                {/* Coverage Area */}
                {selectedMarker.coverageArea && (
                  <div className="bg-gray-50 rounded p-2 mb-3">
                    <p className="text-xs font-semibold text-gray-500 mb-1">
                      {language === 'en' ? 'Coverage Area' : 'Zone de couverture'}
                    </p>
                    <p className="text-sm text-gray-700">{selectedMarker.coverageArea}</p>
                  </div>
                )}

                {/* View details action */}
                <Button
                  size="sm"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewDetails(selectedMarker);
                  }}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {language === 'en' ? 'View details' : 'Voir les détails'}
                </Button>

                {/* Contact Information */}
                <div className="border-t pt-2">
                  <p className="text-xs font-semibold text-gray-500 mb-2">
                    {language === 'en' ? 'Contact Information' : 'Coordonnées'}
                  </p>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-3 h-3 text-gray-400" />
                      <a href={`mailto:${selectedMarker.email}`} className="text-blue-600 hover:underline">
                        {selectedMarker.email}
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-3 h-3 text-gray-400" />
                      <a href={`tel:${selectedMarker.phone}`} className="text-blue-600 hover:underline">
                        {selectedMarker.phone}
                      </a>
                    </div>
                  </div>
                </div>

                {/* Coordinates */}
                <div className="mt-2 pt-2 border-t">
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <MapPin className="w-3 h-3" />
                    <span>{selectedMarker.latitude.toFixed(4)}, {selectedMarker.longitude.toFixed(4)}</span>
                  </div>
                </div>
              </div>
            </InfoWindow>
          )}
        </Map>
      </APIProvider>

      {/* Legend - application-controlled, unchanged from Leaflet version */}
      <Card className="absolute bottom-4 right-4 p-3 bg-white shadow-lg z-[1000]">
        <h4 className="font-semibold text-sm mb-2">
          {language === 'en' ? 'Registration Status' : "Statut d'inscription"}
        </h4>
        <div className="space-y-1">
          {Object.entries(MARKER_COLORS).map(([status, color]) => (
            <div key={status} className="flex items-center space-x-2">
              <div
                style={{
                  backgroundColor: color,
                  width: '14px',
                  height: '14px',
                  borderRadius: '50%',
                  border: '2px solid white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }}
              />
              <span className="text-xs">
                {STATUS_LABELS[language][status]}
              </span>
            </div>
          ))}
        </div>
        <div className="border-t mt-2 pt-2">
          <p className="text-xs text-gray-500">
            {language === 'en' ? 'Click marker for details' : 'Cliquez sur un marqueur pour les détails'}
          </p>
        </div>
      </Card>

      {/* Marker count */}
      <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full shadow-lg z-[1000] text-sm">
        <span className="font-medium">{validMarkers.length}</span>
        <span className="text-gray-500 ml-1">
          {language === 'en' ? 'locations' : 'emplacements'}
        </span>
      </div>
    </div>
  );
};

export default MapView;
